import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { FaCogs } from 'react-icons/fa';
import { GetAllModifiersByLocalId } from '../../../../../../../../../../../../../../crud/menu_api/selection_modifiers';
import ModifierSearchSection from './components/ModifierSearchSection/ModifierSearchSection';
import ModifierListSection from './components/ModifierListSection/ModifierListSection';
import ModifierConfigurationSection from './components/ModifierConfigurationSection/ModifierConfigurationSection';
import ModifierPreviewSection from './components/ModifierPreviewSection/ModifierPreviewSection';
import './ExistingModifierSelector.scss';

interface ExistingModifier {
  modifier_id: number;
  modifier_name: string;
  modifier_description?: string;
  local_id: number;
  items_affected: Array<{
    item_id: number;
    item_name: string;
  }>;
  options_included: Array<{
    option_id: number;
    option_name: string;
    option_description?: string;
    price_modifier: number;
  }>;
  total_items_affected: number;
  total_options: number;
  has_price_modifiers: boolean;
  has_descriptions: boolean;
  options_with_descriptions: number;
}

interface ExistingModifierSelectorProps {
  show: boolean;
  onHide: () => void;
  onModifierSelected: (modifier: any) => void;
  currentModifiers: any[];
  itemName?: string;
}

const ExistingModifierSelector: React.FC<ExistingModifierSelectorProps> = ({
  show,
  onHide,
  onModifierSelected,
  currentModifiers,
  itemName
}) => {
  const [existingModifiers, setExistingModifiers] = useState<ExistingModifier[]>([]);
  const [filteredModifiers, setFilteredModifiers] = useState<ExistingModifier[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedModifier, setSelectedModifier] = useState<ExistingModifier | null>(null);
  const [modifierConfiguration, setModifierConfiguration] = useState({
    is_optional: true,
    is_price_setter: false
  });

  const loadExistingModifiers = useCallback(async () => {
    setLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const localId = user?.local_id || 0;
      
      const response = await GetAllModifiersByLocalId(localId);
      
      if (response.data) {
        // Filter out modifiers that are already added to current item
        const currentModifierIds = currentModifiers.map(m => m.modifier_id).filter(Boolean);
        const availableModifiers = response.data.filter((modifier: ExistingModifier) => 
          !currentModifierIds.includes(modifier.modifier_id)
        );
        
        setExistingModifiers(availableModifiers);
        setFilteredModifiers(availableModifiers);
      }
    } catch (error) {
      console.error('Error loading existing modifiers:', error);
    } finally {
      setLoading(false);
    }
  }, [currentModifiers]);

  useEffect(() => {
    if (show) {
      loadExistingModifiers();
    }
  }, [show, loadExistingModifiers]);

  // Search functionality
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredModifiers(existingModifiers);
      return;
    }

    const searchLower = searchTerm.toLowerCase();
    const filtered = existingModifiers.filter(modifier => 
      modifier.modifier_name.toLowerCase().includes(searchLower) ||
      (modifier.modifier_description && modifier.modifier_description.toLowerCase().includes(searchLower))
    );
    
    setFilteredModifiers(filtered);
  }, [searchTerm, existingModifiers]);

  const handleModifierSelect = (modifier: ExistingModifier) => {
    setSelectedModifier(modifier);
    // Set default configuration
    setModifierConfiguration({
      is_optional: true,
      is_price_setter: false
    });
  };

  const handleConfirmSelection = () => {
    if (selectedModifier) {
      const modifierToAdd = {
        modifier_id: selectedModifier.modifier_id,
        modifier_name: selectedModifier.modifier_name,
        modifier_description: selectedModifier.modifier_description,
        is_new: false,
        options_included: selectedModifier.options_included.map(option => ({
          ...option,
          is_new: false,
          delivery_price_modifier: 0,
          online_price_modifier: 0,
          desk_price_modifier: 0
        })),
        ...modifierConfiguration
      };
      onModifierSelected(modifierToAdd);
      handleClose();
    }
  };

  const handleClose = () => {
    setSelectedModifier(null);
    setSearchTerm('');
    setModifierConfiguration({
      is_optional: true,
      is_price_setter: false
    });
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="xl" className="existing-modifier-selector-modal-clean">
      <Modal.Header>
        <div className="form-modal-title-container">
          <Modal.Title className="form-modal-title">
            <FaCogs className="me-2" />
            Seleccionar Modificador Existente
          </Modal.Title>
          {itemName && (
            <p className="form-modal-subtitle">
              Item base: <span className="current-item">{itemName}</span>
            </p>
          )}
        </div>
        <Button variant="none" className="modal-close-button" onClick={handleClose}>
          ✕
        </Button>
      </Modal.Header>
      
      <Modal.Body>
        {!selectedModifier ? (
          <div className="modifier-selection-container">
            <ModifierSearchSection 
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
            />
            
            <ModifierListSection
              loading={loading}
              filteredModifiers={filteredModifiers}
              selectedModifier={selectedModifier}
              onModifierSelect={handleModifierSelect}
              searchTerm={searchTerm}
            />
          </div>
        ) : (
          <div className="modifier-configuration-container">
            <ModifierConfigurationSection
              modifierConfiguration={modifierConfiguration}
              setModifierConfiguration={setModifierConfiguration}
              selectedModifierName={selectedModifier.modifier_name}
              currentItemName={itemName}
            />

            <ModifierPreviewSection 
              selectedModifier={selectedModifier}
            />
          </div>
        )}
      </Modal.Body>
      
      <Modal.Footer>
        <div className="form-modal-footer-custom">
          {!selectedModifier ? (
            <>
              <Button 
                variant="outline-secondary" 
                onClick={handleClose}
                className="form-button-cancel"
              >
                Cancelar
              </Button>
              <Button 
                variant="primary" 
                disabled={!selectedModifier}
                onClick={handleConfirmSelection}
                className="form-button-success"
              >
                Seleccionar Modificador
              </Button>
            </>
          ) : (
            <>
              <Button 
                variant="outline-secondary" 
                onClick={() => setSelectedModifier(null)}
                className="form-button-cancel"
              >
                Volver a Lista
              </Button>
              <Button 
                variant="primary" 
                onClick={handleConfirmSelection}
                className="form-button-success"
              >
                Confirmar y Agregar
              </Button>
            </>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default ExistingModifierSelector;
